import { Box, Stack } from "@mui/material";
import dayjs from "dayjs";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useInViewRef } from "rooks";
import { CachePolicies } from "use-http";

import { SkeletonIndicator } from "components/common/AnimatedWebcam/SkeletonIndicator";
import { DetectorEnabledSwitch } from "components/detector/DetectorEnabledSwitch";
import {
  DetectorMetricsTable,
  DetectorMetricsTableRowData,
} from "components/detector/DetectorStats/DetectorMetricsTable/DetectorMetricsTable";
import { DetectorTableCell } from "components/detector/DetectorStats/DetectorMetricsTable/types";
import { generateArrayWithMax } from "components/detector/DetectorStats/TopLevelStats/CountTopLevelStats";
import { getTotalValue } from "components/detector/DetectorStats/TopLevelStats/utils";
import { PageContext } from "components/detector/ImageQueryPreview";
import { ImageQueryView } from "components/detector/ImageQueryView";
import { getConfig } from "config/config";
import { DETECTOR_MODE_TO_ABBREVIATED_NAME, STABLE_DETECTOR_MODES } from "config/constants";
import { OTHER_LABEL } from "config/LabelMapping";
import { MetricClassCounts, Metrics } from "hooks/DetectorDetail/useTopLevelStats";
import { useFetchCustom } from "hooks/useFetchCustom";
import { Typography } from "new-components/typography/Typography";
import { DetectorModes } from "types/createDetector";
import { DetectorBase } from "types/detectors";
import { DATE_FORMAT_SLASH } from "utils/date";
import { getTitleSuffix } from "utils/imageQueriesUtils";
import { FilterOptionsLabel } from "utils/redirectDataPage";

const config = getConfig();

const { JANZU_URL } = config;

const cellClasses: Record<string, string> = {
  YES: "text-hl-purple",
  NO: "text-hl-magenta",
  UNCLEAR: "text-neutral-400",
  "NO TEXT": "text-primary-dark",
  [OTHER_LABEL]: "text-primary-dark",
};
interface DetectorItemProps {
  detector: DetectorBase;
}

export function DetectorItem(props: DetectorItemProps) {
  const { detector } = props;
  const navigate = useNavigate();
  const location = useLocation();
  const [myRef, inView] = useInViewRef();
  const [isVisibleOnce, setIsVisibleOnce] = useState(false);

  // Carry the detector list's current filter query string so the detail page's
  // back button can return to the list with the same filters/sort applied.
  const handleNavigateToDetectorDetails = useCallback(
    () =>
      navigate(`/reef/detectors/${detector.id}`, {
        state: { detectorListSearch: location.search },
      }),
    [navigate, detector.id, location.search]
  );

  const {
    get: getMetrics,
    data: metrics,
    loading: loadingMetrics,
  } = useFetchCustom<Metrics>(
    `${JANZU_URL}device-api/metrics/detectors/${detector.id}?type=summary&answer_type=current_best_answer`,
    {
      cachePolicy: CachePolicies.NO_CACHE,
    }
  );

  useEffect(() => {
    if (inView && !isVisibleOnce) {
      getMetrics().then(() => setIsVisibleOnce(true));
    }
  }, [getMetrics, inView, isVisibleOnce]);

  const generateMetricCountColums = useCallback(
    () =>
      detector.mode === DetectorModes.COUNT && metrics?.summary?.max_count
        ? generateArrayWithMax(metrics?.summary?.max_count)
        : [],
    [detector.mode, metrics?.summary?.max_count]
  );

  const BINARY_METRICS_COLUMNS = useMemo(() => ["YES", "NO", "UNCLEAR"], []);
  const COUNT_METRICS_COLUMNS = generateMetricCountColums();
  const MULTICHOICE_METRICS_COLUMNS = useMemo(() => (metrics && metrics.summary?.class_names) || [], [metrics]);

  const METRICS_COLUMNS = useMemo(
    () => ({
      [DetectorModes.BINARY]: BINARY_METRICS_COLUMNS,
      [DetectorModes.COUNT]: COUNT_METRICS_COLUMNS,
      [DetectorModes.TEXT_RECOGNITION]: ["NO_TEXT", "HAS_TEXT", "UNCLEAR"],
      [DetectorModes.MULTICHOICE]: [...MULTICHOICE_METRICS_COLUMNS, "UNCLEAR"],
      [DetectorModes.BOUNDING_BOX]: ["NO_OBJECTS", "BOUNDING_BOX", "UNCLEAR"],
    }),
    [BINARY_METRICS_COLUMNS, COUNT_METRICS_COLUMNS, MULTICHOICE_METRICS_COLUMNS]
  );

  const COLUMNS = [...METRICS_COLUMNS[detector.mode], "TOTAL"];

  const getRowColumns = useCallback(
    (metric: MetricClassCounts) => {
      if (!metric) return [];
      const columns: DetectorTableCell[] = METRICS_COLUMNS[detector.mode].map((key) => ({
        key,
        value: metric[key] ?? 0,
        className: cellClasses[key] ?? "text-primary-light",
      }));

      const totalValue = getTotalValue(metric);

      columns.push({
        key: "TOTAL",
        value: totalValue,
      });
      return columns;
    },
    [detector.mode, METRICS_COLUMNS]
  );

  const generateValuesRow = useCallback(
    (label: FilterOptionsLabel, metric: MetricClassCounts) => ({
      label,
      cells: [...getRowColumns(metric)],
    }),
    [getRowColumns]
  );

  const rowsData: DetectorMetricsTableRowData[] = useMemo(() => {
    if (!metrics) return [];

    return [
      {
        ...generateValuesRow("Total Answered", metrics.summary.class_counts.total),
        textClass: "font-bold",
      },
    ];
  }, [metrics, generateValuesRow]);

  return (
    <Box
      id="detector-card"
      ref={myRef}
      className="flex h-[191px] sm:h-[241px] md:h-[241px] p-[10px] md:p-[20px]"
      sx={{
        borderBottom: "1px solid #CED0D1",
        cursor: "pointer"
      }}
      onClick={handleNavigateToDetectorDetails}
    >
      <Stack spacing={{ xs: "5px", sm: "20px", md: "20px" }} direction="row" className="w-full">
        <Stack id="image-section" className="h-full w-[227px] hidden sm:block">
          {detector.last_image_query ? (
            <ImageQueryView
              imageQuery={detector.last_image_query}
              titleSuffix={getTitleSuffix(detector.last_image_query, detector.mode, PageContext.DetectorDetails)}
              showTimestamp
            />
          ) : (
            <Box
              sx={{
                display: "flex",
                width: "100%",
                height: "100%",
                justifyContent: "center",
                alignItems: "center",
                backgroundColor: "rgba(0, 0, 0, 0.11)",
              }}
            >
                <Typography variant="subtitle" align="center" className="mx-[25px]">
                No user-labeled examples are available
              </Typography>
            </Box>
          )}
        </Stack>

        <Stack
          id="data-section"
          spacing={{ xs: "auto" }}
          className="w-[calc(100%-110px)] sm:w-[calc(100%-367px)] md:w-[calc(100%-467px)]"
        >
          <Box className="flex w-full h-[24px] gap-[20px]">
            <Typography className="text-primary-darker w-full" noWrap>
              {detector.name}
            </Typography>
            <Box className="hidden md:block w-[300px] text-right flex-shrink-0">
              <Typography variant="small" className="text-neutral-medium">
                CREATED BY {detector.created_by} ON {dayjs(detector.created_at).format(DATE_FORMAT_SLASH)}
              </Typography>
              <Typography variant="small" className="text-neutral-medium">
                DETECTOR ID: {detector.id}
              </Typography>
            </Box>
          </Box>

          <Box className="flex w-full h-[56px]">
            <Typography fontWeight="semibold" className="text-primary-darker mb-[8px] line-clamp-2">
              {detector.label_prompt}
            </Typography>
          </Box>

          <Box>
            {loadingMetrics || !metrics ? (
              SkeletonIndicator({ loading: true, height: 70 })
            ) : (
              <DetectorMetricsTable
                columns={COLUMNS}
                rowsData={rowsData}
                maxCount={metrics?.summary?.max_count}
                showBottomBorder={false}
              />
            )}
          </Box>
        </Stack>

        <Stack id="actions-section" className="w-[100px] md:w-[200px] justify-between">
          <Box
            className="flex w-full justify-start"
            sx={{
              borderBottom: "1px solid #e0e0e0",
              gap: { xs: "0px", sm: "8px" },
            }}
          >
            <Typography
              fontWeight="bold"
              textTransform="uppercase"
              className="text-neutral-medium w-[calc(100%-38px)]"
              noWrap
            >
              {DETECTOR_MODE_TO_ABBREVIATED_NAME[detector.mode]}
            </Typography>
            {!STABLE_DETECTOR_MODES.includes(detector.mode) && (
              <Typography variant="small" fontWeight="bold" className="text-neutral-dark w-[38px]">
                (BETA)
              </Typography>
            )}
          </Box>
          {!detector.is_explore_detector && (
            <Box className="w-full h-[51px] flex items-center justify-center" sx={{ borderTop: "1px solid #e0e0e0" }}>
              <DetectorEnabledSwitch acceptingNewQueriesValue={detector.status} detectorId={detector.id} />
            </Box>
          )}
        </Stack>
      </Stack>
    </Box>
  );
}
